import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarCheck, Activity, CircleCheck, CircleX, ReceiptIndianRupee, UserPlus,
  Plus, UserRound, FileText, Award, type LucideIcon,
} from "lucide-react";
import { useDashboardStats } from "@/lib/queries";
import { fmtINR, fmtTime } from "@emr/shared/india";
import { useAuth } from "@/context/AuthContext";
import { Has } from "@/components/Has";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AppointmentStatusBadge } from "@/components/StatusBadge";
import { BookDialog } from "@/features/appointments/BookDialog";
import { CheckInDialog, type CheckInTarget } from "@/features/appointments/CheckInDialog";
import { useState } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

function KpiCard({ icon: Icon, label, value, accent }: { icon: LucideIcon; label: string; value: React.ReactNode; accent?: string }) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent ?? "bg-cream-100 text-maroon-700"}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-muted">{label}</div>
          <div className="truncate text-2xl font-bold text-ink dark:text-cream-50">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { data, isLoading } = useDashboardStats();
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const [bookOpen, setBookOpen] = useState(false);
  const [checkInTarget, setCheckInTarget] = useState<CheckInTarget | null>(null);
  const qc = useQueryClient();

  const canCheckIn = isAdmin || permissions.has("appointments.edit");

  const setStatus = async (apptId: string, status: "Completed" | "Cancelled") => {
    if (!window.confirm(`Mark this appointment ${status.toLowerCase()}?`)) return;
    try {
      await apiFetch(`/appointments/${apptId}`, { method: "PATCH", body: JSON.stringify({ status }) });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
    } catch (e) {
      const err = e as Error & { body?: { error?: string } };
      toast.error(err?.body?.error || "Failed to update");
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Dashboard</h1>
        <div className="flex gap-2">
          <Has perm="appointments.create">
            <Button onClick={() => setBookOpen(true)} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              <Plus size={16} className="mr-1" /> New Appointment
            </Button>
          </Has>
          <Has perm="patients.create">
            <Button variant="outline" onClick={() => navigate("/patients/new")}>
              <UserRound size={16} className="mr-1" /> New Patient
            </Button>
          </Has>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
        ) : (
          <>
            <KpiCard icon={CalendarCheck} label="Today's appointments" value={data?.kpis.todaysAppointments ?? 0} />
            <KpiCard icon={Activity} label="Checked-in now" value={data?.kpis.checkedInNow ?? 0} accent="bg-green-100 text-green-800" />
            <KpiCard icon={CircleCheck} label="Completed today" value={data?.kpis.completedToday ?? 0} accent="bg-green-100 text-green-800" />
            <KpiCard icon={CircleX} label="Cancelled/No-show" value={data?.kpis.cancelledNoShowToday ?? 0} accent="bg-red-100 text-red-700" />
            <KpiCard icon={ReceiptIndianRupee} label="Pending invoices" value={data?.kpis.pendingInvoicesCount ?? 0} accent="bg-amber-100 text-amber-800" />
            <KpiCard icon={UserPlus} label="New patients (month)" value={data?.kpis.newPatientsThisMonth ?? 0} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-maroon-700 dark:text-amber-500">Today's timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
              ) : (data?.timeline?.length ?? 0) === 0 ? (
                <div className="p-8 text-center text-sm text-muted">No appointments scheduled for today.</div>
              ) : (
                <ul className="divide-y divide-cream-100 dark:divide-dark-700">
                  {data?.timeline.map((t) => (
                    <li
                      key={t.appointmentId}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-cream-50/50 cursor-pointer"
                      onClick={() => t.patientId && navigate(`/patients/${t.patientId}`)}
                    >
                      <div className="w-16 shrink-0 text-sm font-medium text-muted">{t.arrivalTime ? fmtTime(t.arrivalTime) : "—"}</div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-ink dark:text-cream-50">{t.patientName ?? "Unknown"}</div>
                        <div className="truncate text-xs text-muted">{t.clinicUhid} · {t.doctorName ?? "—"}</div>
                      </div>
                      <AppointmentStatusBadge status={t.status} />
                      {canCheckIn && t.status === "Booked" && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setCheckInTarget({ appointmentId: t.appointmentId, patientId: t.patientId ?? "", doctorId: t.doctorId, patientName: t.patientName, clinicUhid: t.clinicUhid, photoR2Key: t.photoR2Key ?? null, slot: t.slot, arrivalTime: t.arrivalTime }); }}>
                          Check-in
                        </Button>
                      )}
                      {canCheckIn && (t.status === "CheckedIn" || t.status === "Booked") && (
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setStatus(t.appointmentId, "Completed"); }}>
                          <CircleCheck size={14} />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-maroon-700 dark:text-amber-500">Pending tasks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="mb-1 text-xs font-medium text-muted">Unpaid invoices ({data?.pendingTasks.unpaidInvoices.length ?? 0})</div>
                {data?.pendingTasks.unpaidInvoices.slice(0, 5).map((inv) => (
                  <div key={inv.id} className="flex justify-between py-1">
                    <span className="truncate">{inv.number} · {inv.patientName ?? "—"}</span>
                    <span className="text-red-600">{fmtINR(inv.totalPaise - inv.paidPaise)}</span>
                  </div>
                ))}
                {(data?.pendingTasks.unpaidInvoices.length ?? 0) === 0 && <div className="text-xs text-muted">None</div>}
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-muted">Pending review appointments ({data?.pendingTasks.pendingReviewAppts.length ?? 0})</div>
                {data?.pendingTasks.pendingReviewAppts.slice(0, 5).map((a) => (
                  <div key={a.id} className="py-1 cursor-pointer hover:text-maroon-700" onClick={() => navigate("/appointments")}>
                    {a.patientName ?? "—"} · {a.clinicUhid ?? "—"}
                  </div>
                ))}
                {(data?.pendingTasks.pendingReviewAppts.length ?? 0) === 0 && <div className="text-xs text-muted">None</div>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-maroon-700 dark:text-amber-500">Quick actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <Has perm="appointments.create">
                <Button variant="outline" size="sm" onClick={() => setBookOpen(true)}><CalendarCheck size={14} className="mr-1" /> Appointment</Button>
              </Has>
              <Has perm="patients.create">
                <Button variant="outline" size="sm" onClick={() => navigate("/patients/new")}><UserRound size={14} className="mr-1" /> Patient</Button>
              </Has>
              <Button variant="outline" size="sm" disabled><ReceiptIndianRupee size={14} className="mr-1" /> Invoice</Button>
              <Button variant="outline" size="sm" disabled><FileText size={14} className="mr-1" /> Referral</Button>
              <Button variant="outline" size="sm" disabled><Award size={14} className="mr-1" /> Certificate</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <BookDialog open={bookOpen} onOpenChange={setBookOpen} />
      <CheckInDialog open={checkInTarget !== null} onOpenChange={(v) => { if (!v) setCheckInTarget(null); }} target={checkInTarget} />
    </div>
  );
}
